<template>
  <dd-header-card header="Auto verzekering vergelijken">
    <CompareComponent
        title="Autoverzekering vergelijken"
        compare-type="car"
        :route-params="{policy_id: policyId, customer_id:customerId}"
        :attribute-config="attributeConfig"
        show-result-table
        :table-headers="[
            {
              text: 'Vergelijk',
              value: 'compare'
            },
            {
              text: 'Logo',
              value: 'logo'
            },
            {
              text: 'Productnaam',
              value: 'Productnaam'
            },
            {
              text: 'Huidige premie',
              value: 'CurrentPremiumAmount'
            },
            {
              text: 'Premiebedrag',
              value: 'Premiebedrag'
            },
            {
              text: 'Besparing',
              value: 'Besparing'
            },
        ]"
        result-key-for-table="Premieobjecten[0].Premies"
        :sort-result-function="sortFunction"
        result-errors-key="Acceptatieproblemen"
        @result-received="resultReceived"
    >
      <template
          v-slot:input="{calculationValues,
          overrides,
          getRequiredFields,
          getOverridesValue,
          editOverridesValue, getPrettyName, getType, getErrors, getEnumOptions, getSimplesFromCollection,
          getNotEditableFields}"
      >
        <a-row :gutter="16">
          <a-col :span="12">
            <EditableValuesList title="Regelmatige bestuurder"
                                :values="getSimplesFromCollection(calculationValues['Regelmatigebestuurder'])"
                                :attribute-config="attributeConfig"
                                :get-overrides-value="getOverridesValue"
                                :edit-overrides-value="editOverridesValue"
                                :get-errors="getErrors"
                                attribute-pointer-prefix="Regelmatigebestuurder"
                                :required-fields="getRequiredFields(calculationValues['Regelmatigebestuurder'])"
                                :not-editable-fields="getNotEditableFields(calculationValues)"
            ></EditableValuesList>
            <EditableValuesList title="Voertuig"
                                :values="getSimplesFromCollection(calculationValues['Voertuig'])"
                                :attribute-config="attributeConfig"
                                :get-overrides-value="getOverridesValue"
                                :edit-overrides-value="editOverridesValue"
                                :get-errors="getErrors"
                                attribute-pointer-prefix="Voertuig"
                                :required-fields="getRequiredFields(calculationValues['Voertuig'])"
                                :not-editable-fields="getNotEditableFields(calculationValues)"
            >
              <template v-slot:Kenteken.default="{ defaultValue }">
                <LicensePlate :license-plate="defaultValue"></LicensePlate>
              </template>
            </EditableValuesList>
            <EditableValuesList title="Nieuwepolis"
                                :values="getSimplesFromCollection(calculationValues['Nieuwepolis'])"
                                :attribute-config="attributeConfig"
                                :get-overrides-value="getOverridesValue"
                                :edit-overrides-value="editOverridesValue"
                                :get-errors="getErrors"
                                attribute-pointer-prefix="Nieuwepolis"
                                :required-fields="getRequiredFields(calculationValues['Nieuwepolis'])"
                                :not-editable-fields="getNotEditableFields(calculationValues)"
            ></EditableValuesList>
          </a-col>
          <a-col :span="12">
            <span
                v-if="calculationValues['Premieobjecten'] && Array.isArray(calculationValues['Premieobjecten']['Premieobject'])"
            >
              <EditableValuesList
                  v-for="(premieObject, key) in calculationValues['Premieobjecten']['Premieobject']"
                  :key="key"
                  :title="`Premieobject ${key + 1}`"
                  :values="getSimplesFromCollection(premieObject)"
                  :required-fields="getRequiredFields(premieObject)"
                  :attribute-config="attributeConfig"
                  :get-overrides-value="getOverridesValue"
                  :edit-overrides-value="editOverridesValue"
                  :get-errors="getErrors"
                  :attribute-pointer-prefix="`Premieobjecten.Premieobject.${key}`"
                  :not-editable-fields="getNotEditableFields(calculationValues)"
              ></EditableValuesList>
            </span>
            <EditableValuesList
                v-else-if="calculationValues['Premieobjecten'] && calculationValues['Premieobjecten']['Premieobject']"
                title="Premieobject"
                :values="getSimplesFromCollection(calculationValues['Premieobjecten']['Premieobject'])"
                :required-fields="getRequiredFields(calculationValues['Premieobjecten']['Premieobject'])"
                :attribute-config="attributeConfig"
                :get-overrides-value="getOverridesValue"
                :edit-overrides-value="editOverridesValue"
                :get-errors="getErrors"
                attribute-pointer-prefix="Premieobjecten.Premieobject"
                :not-editable-fields="getNotEditableFields(calculationValues)"
            ></EditableValuesList>
          </a-col>
        </a-row>
      </template>
      <template v-slot:pre-table>
        <a-tooltip style="margin-bottom: 1em;">
          <template slot="title">
            <span v-if="canCompare">
              Verkrijg een gedetailleerde vergelijking tussen de geselecteerde premies.
            </span>
            <span v-else>
              Selecteer twee of drie premies om te vergelijken
            </span>
          </template>
          <a-button type="success" @click="compareCarPremiums" :disabled="!canCompare" :loading="loadingCompareCarPremiums">
            Vergelijk
          </a-button>
        </a-tooltip>
      </template>
      <template v-slot:table.compare="{option}">
        <a-tooltip>
          <template slot="title" v-if="!canSelectMore">
            Maximaal drie premies kunnen tegelijk worden vergeleken.
          </template>
          <a-checkbox
              @change="toggleSelected($event.target.checked, option)"
              :disabled="!canSelectMore && !isSelected(option)"
          ></a-checkbox>
        </a-tooltip>
      </template>
      <template v-slot:table.logo="{option}">
        <lazy-component>
          <object :data="`/web_api/v1/compare/car/images/${option.Productid}`"
                  type="image/png"
                  style="width:100px">
            Geen afbeelding beschikbaar
          </object>
        </lazy-component>
      </template>
      <template v-slot:table.Premiebedrag="{value}">
        {{ value | toCurrency }}
      </template>
      <template v-slot:table.CurrentPremiumAmount="{result}">
        {{ result.currentPremiumAmount | toCurrency }}
      </template>
      <template v-slot:table.Besparing="{option, result}">
        <span :class="(result.currentPremiumAmount - option.Premiebedrag) < 0 ? 'text-danger' : 'text-success'">
          {{ (result.currentPremiumAmount - option.Premiebedrag) | toCurrency }}
        </span>
      </template>
    </CompareComponent>

    <CompareCarPremiumModal
        v-model="compareCarPremiumModalOpen"
        :rules="comparisonRules"
        :comparison-premiums="Object.values(selectedForComparison)"
    ></CompareCarPremiumModal>
  </dd-header-card>
</template>

<script>
import CompareComponent from "./CompareComponent";
import EditableValuesList from "./EditableValuesList";
import compareApi from '~/api/compare.js';
import CompareCarPremiumModal from "./CompareCarPremiumModal";

export default {
  name: 'CarInsurance',
  props: {
    customerId: [String, Number],
    policyId: [String, Number]
  },
  components: {
    CompareComponent,
    EditableValuesList,
    CompareCarPremiumModal
  },
  data() {
    return {
      errorModal: {},
      attributeConfig: {
        Geboortedatum: {
          prettyName: 'Geboortedatum',
          type: 'date'
        },
        Postcode: {
          prettyName: 'Postcode'
        },
        Huisnummer: {
          prettyName: 'Huisnummer'
        },
        Huisnummertoevoeging: {
          prettyName: 'Huisnummertoevoeging'
        },
        Jarenschadevrij: {
          prettyName: 'Aantal schadenvrije jaren',
          type: 'integer'
        },
        Jarenverzekerd: {
          prettyName: 'Aantal verzekerde jaren',
          type: 'integer',
          info: 'Deze waarde wordt als volgt berekend: leeftijd - 18'
        },
        Natuurlijkpersoon: {
          prettyName: 'Natuurlijkpersoon',
          type: 'boolean'
        },
        Beveiliging: {
          prettyName: 'Beveiliging',
          type: 'integer'
        },
        Waardeaccessoires: {
          prettyName: 'Waarde accessoires',
          type: 'euro'
        },
        Waardeaudio: {
          prettyName: 'Waarde audio',
          type: 'euro'
        },
        Kenteken: {
          prettyName: 'Kenteken',
        },
        Ingangsdatum: {
          prettyName: 'Ingangsdatum',
          type: 'date'
        },
        Particuliergebruik: {
          prettyName: 'Particuliergebruik',
          type: 'boolean'
        },
        Kilometrage: {
          prettyName: 'Kilometrage',
          type: 'integer'
        },
        Inclbtw: {
          prettyName: 'btw inclusief',
          type: 'boolean',
          info: 'per default wordt er inclusief BTW gerekend'
        },
        Betalingstermijn: {
          prettyName: 'Betalingstermijn',
          type: 'enum',
          enumOptions: {
            maand: 'Maand',
            kwartaal: 'Kwartaal',
            halfjaar: 'Halfjaar',
            jaar: 'Jaar'
          }
        },
        Contractsduur: {
          prettyName: 'Contractsduur',
          type: 'integer'
        },
        Dekking: {
          prettyName: 'Dekking',
          type: 'enum',
          enumOptions: {
            wa: 'Wettelijke Aansprakelijkheid',
            bc: 'Beperkt Casco',
            vc: 'Volledig Casco'
          }
        },
        Gewensteigenrisico: {
          prettyName: 'Gewenst eigenrisico',
          type: 'euro'
        },
        Poliskostenincl: {
          prettyName: 'Poliskosten inclusief',
          type: 'boolean'
        },
        Assurantiebelastingincl: {
          prettyName: 'Assurantiebelasting inclusief',
          type: 'boolean'
        },
        Prolongatiekostenincl: {
          prettyName: 'Prolongatiekosten inclusief',
          type: 'boolean'
        },
        Premiebedrag: {
          prettyName: 'Premiebedrag',
          type: 'euro'
        },
        Assurantiebelasting: {
          prettyName: 'Assurantiebelasting',
          type: 'euro'
        },
        created_at: {
          prettyName: 'Aangemaakt op',
          type: 'date'
        },
      },
      selectedForComparison: {},
      coverage: null,
      compareCarPremiumModalOpen: false,
      comparisonRules: [],
      loadingCompareCarPremiums: false
    }
  },
  methods: {
    sortFunction(result) {
      let premiumSorted = result.sort((a, b) => {
        return b.Premiebedrag - a.Premiebedrag;
      })

      return premiumSorted.sort((a, b) => {
        return a.Acceptatie === 'nee' || a.Acceptatie === 0 || a.Acceptatie === false ? 1 : -1;
      })
    },
    toggleSelected(selected, option) {
      if (selected) {
        this.$set(this.selectedForComparison, option.Productid, option);
      } else {
        this.$delete(this.selectedForComparison, option.Productid)
      }
    },
    async compareCarPremiums() {
      let params = {};
      let selectedForComparison = Object.values(this.selectedForComparison)

      params.product_id_1 = selectedForComparison[0].Productid;
      params.product_id_2 = selectedForComparison[1].Productid;
      params.product_id_3 = selectedForComparison.length > 2 ? selectedForComparison[2].Productid : null;
      params.coverage_1 = this.coverage;
      params.coverage_2 = this.coverage;
      params.coverage_3 = selectedForComparison.length > 2 ? this.coverage : null;

      this.loadingCompareCarPremiums = true;

      let result = await compareApi.compareCarPremiums(params)

      if (result.hasOwnProperty('data')
          && result.data.hasOwnProperty('Polisvergelijking')
          && result.data.Polisvergelijking.hasOwnProperty('Regels')
      ) {
        this.comparisonRules = result.data.Polisvergelijking.Regels;
        this.compareCarPremiumModalOpen = true;
      } else {
        this.$notification['warning']({
          message: 'Mislukt',
          description: 'Het vergelijken is mislukt.',
        });
      }

      this.loadingCompareCarPremiums = false;
    },
    isSelected(option) {
      return Object.keys(this.selectedForComparison).includes(String(option.Productid));
    },
    resultReceived(result) {
      this.coverage = result.coverage;
      this.selectedForComparison = {};
    }
  },
  computed: {
    canSelectMore() {
      return Object.keys(this.selectedForComparison).length < 3;
    },
    canCompare() {
      return Object.keys(this.selectedForComparison).length > 1;
    }
  }
}
</script>

<style scoped>

</style>
